import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { uuid as uuidV4 } from 'uuidv4';
import axios from 'axios';
import * as Locales from 'date-fns/locale';
import {
  format as tzFormat,
  utcToZonedTime,
  zonedTimeToUtc
} from 'date-fns-tz';
import {
  isSameDay,
  isAfter,
  isBefore,
  parse,
  isValid,
  parseISO
} from 'date-fns';

// constants
import packageJSON from '../../package.json';
import LocalStrings from '../assets/languages';
import { showError } from '../components/Common/alertmessage';
import {
  CDN_URL,
  PROJECT_URI,
  FILE_TYPES,
  PORTAL_LOCALSTORAGE_ACCESS_KEY,
  LOCAL_TIMEZONE_LOCALE,
  LOCAL_TIMEZONE,
  LEAD_FORM_AUDIO_PREFIX,
  IS_API4,
  SCAN_GENERATE_URL,
  INITIAL_APP_LANGUAGE,
  WIDGET_URL,
  ENC_FILES_URL,
  SCRIPT_URI_ADDON,
  FILES_URL,
  INSTANCE_URL,
  SOCIAL_BOOST_QUERY_URL,
  SESSION_NO_TRACK
} from '../constants/constant';
import {
  ALLOWED_FEATURE_KEYS,
  ALLOWED_THUMB_TYPES,
  MULTI_TAB_FEATURES
} from '../constants/section-constants/common-constants';
import {
  ALLOWED_ADVERTISEMENT_REDIRECTIONS,
  ALLOWED_ATTENDEE_REDIRECTIONS,
  ALLOWED_BOOKMARK_REDIRECTIONS,
  ALLOWED_COMMUNITY_REDIRECTIONS,
  ALLOWED_GALLERY_REDIRECTIONS,
  ALLOWED_GLOBALSEARCH_REDIRECTIONS,
  ALLOWED_MAPS_REDIRECTIONS,
  ALLOWED_MYSCAN_REDIRECTIONS,
  ALLOWED_NOTES_REDIRECTIONS,
  ALLOWED_NOTIFICATION_REDIRECTIONS,
  ALLOWED_SESSION_REDIRECTIONS,
  ALLOWED_SOCIAL_REDIRECTIONS,
  ALLOWED_SPEAKER_REDIRECTIONS,
  ROLE_PRIVILEGE_CHECKING_CASES,
  ROLE_PRIVILEGE_SPECIAL_CASES
} from '../constants/section-constants/redirection-constants';
import {
  ALLOWED_APP_THEMES,
  ALLOWED_SCHEDULE_VIEWS,
  GAMIFICATION_CHALLENGE_VISIBILITY,
  SCHEDULE_LOCALSTORAGE_STRUCTURE
} from '../constants/section-constants/type-constants';
import { loadFromLocalDB, setInLocalDB } from '../reducers/localStorage';
import { ALLOWED_FEATURED_TYPES } from '../constants/section-constants/type-constants';
import {
  ALLOWED_FORM_LAYOUTS,
  ALLOWED_FORM_PROFILE_IMAGE
} from '../constants/section-constants/editprofile-constants';

//files
import assets from '../assets/img/file-icons/index';
import NoImgIcon from '../assets/img/placeholders/NoImg.svg';
import NoAvatarImg from '../assets/img/placeholders/no-avatar.png';

export const OutsideClickDetector = (ref, isOpenned) => {
  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    const handleOutsideClick = e => {
      if (ref.current && !ref.current.contains(e.target) && isOpenned)
        setIsClicked(true);
      else setIsClicked(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [ref, isOpenned]);

  return isClicked;
};

export const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    let id = setInterval(() => {
      savedCallback.current();
    }, delay);
    return () => clearInterval(id);
  }, [delay]);
};

export const isJSONParsable = strValue => {
  try {
    JSON.parse(strValue);
  } catch (e) {
    return false;
  }

  return true;
};

export const isValidDate = dateString => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

export const handleDFQuoteMessage = strValue => {
  // Decode HTML entities
  const decodedString = document.createElement('textarea');
  decodedString.innerHTML = strValue;
  const decodedText = decodedString.value;

  // Parse the JSON string
  try {
    const jsonData = JSON.parse(decodedText);

    return { success: 1, data: jsonData };
  } catch (error) {
    return { success: 0, data: null };
  }
};

/**
 * @param path complete file path
 * @returns filename_uuid.extension
 */
export const getFileName = path => {
  if (_.isEmpty(path) || typeof path !== 'string') return path;
  return path.split('/').pop() ?? path;
};

/**
 * @param path complete file path
 * @returns filename.ext(.enc)
 */
export const getFileNameFromPath = (path = '') => {
  if (_.isEmpty(path) || typeof path !== 'string') return path;

  const fileSection = getFileName(path);
  const hashedName =
    fileSection.substring(0, fileSection.lastIndexOf('_')) ?? '';
  const ext = getExtension(fileSection);

  return `${hashedName}.${ext}`;
};

/**
 * @param fileName filename.ext(.enc)
 * @returns .ext(.enc)
 */
export const getExtension = fileName => {
  const isENC = _.includes(fileName, '.enc');
  const dotSplit = fileName.split('.');
  const ext = isENC
    ? dotSplit[dotSplit.length - 2]
    : dotSplit[dotSplit.length - 1];

  return `${ext}${isENC ? '.enc' : ''}`;
};

export const checkAsset = url => {
  var request = new XMLHttpRequest(); // req var
  request.open('GET', url, true); // as GET
  request.send(); // sent
  request.onload = () => {
    // response got
    // checks for image response size is not null or blank arr, receives string data in KB size
    return !_.isEmpty(request.response);
  };
};

export const isFileENC = fileName => _.includes(fileName, '.enc');

export const isImage = filename => {
  if (_.includes(FILE_TYPES.image, getExtension(filename).toLowerCase()))
    return true;
  else return false;
};

export const isVideo = filename => {
  if (_.includes(FILE_TYPES.video, getExtension(filename).toLowerCase()))
    return true;
  else return false;
};

export const isAudio = filename =>
  getExtension(filename).toLowerCase() == 'mp3';

export const isPDF = filename => getExtension(filename).toLowerCase() == 'pdf';

/**
 * @param itemArr => Contains items to loop through. [{fname: 'Test', lname: 'Name'}, {fname: 'Test2', lname: 'Name2'}]
 * @param limit => Till which element items are allowed. 1
 * @param fields => Aan array of fields of item to go through. ['fname', 'lname']
 * @returns string "Test Name + 1 More"
 */
export const handleStringItemsWithMoreText = (itemArr, limit, fields) => {
  let returnString = [];
  let moreCount = 0;

  _.map(itemArr, (item, itemIndex) => {
    if (itemIndex < limit) {
      let concatString = [];

      _.map(fields, fieldItem => {
        concatString.push(item[fieldItem]);
      });

      returnString.push(concatString.join(' '));
    } else moreCount++;
  });

  return `${returnString.join(', ')}${
    moreCount ? ` +${moreCount} ${LocalStrings.dynamic.KMoreText}` : ''
  }`;
};

export const getCapitalizedText = text => {
  if (text.includes('_')) {
    const words = text.split('_');
    words[0] = _.startCase(words[0]);
    return words.join(' ');
  } else return text;
};

/**
 * @param data  info object
 * @param source  exhibitor|exhibitor-banner|speaker|sponsor|news
 * @returns image path | placeholder svg
 */
export const renderProfileSrc = (data, source) => {
  let returnVal =
    source === ALLOWED_FEATURE_KEYS.attendee ? NoAvatarImg : NoImgIcon;

  switch (source) {
    case ALLOWED_FEATURE_KEYS.exhibitor:
    case ALLOWED_FEATURE_KEYS.speaker:
      if (!_.isEmpty(data?.profileimage))
        returnVal = `${CDN_URL}${getThumbImageUrl(
          data?.profileimage,
          ALLOWED_THUMB_TYPES.md
        )}`;
      break;

    case ALLOWED_FEATURE_KEYS.attendee:
      if (!_.isEmpty(data?.profileurl))
        returnVal = `${CDN_URL}${getThumbImageUrl(data?.profileurl, null)}`;
      break;

    case `${ALLOWED_FEATURE_KEYS.exhibitor}-banner`:
    case `${ALLOWED_FEATURE_KEYS.sponsor}-banner`:
      if (!_.isEmpty(data?.bannerimage))
        returnVal = `${CDN_URL}${getThumbImageUrl(
          data?.bannerimage,
          ALLOWED_THUMB_TYPES.md
        )}`;
      else if (!_.isEmpty(data?.profileimage))
        returnVal = `${CDN_URL}${getThumbImageUrl(
          data?.profileimage,
          ALLOWED_THUMB_TYPES.md
        )}`;
      break;

    case ALLOWED_FEATURE_KEYS.sponsor:
      if (!_.isEmpty(data?.logoimgpath))
        returnVal = `${CDN_URL}${getThumbImageUrl(
          data?.logoimgpath,
          ALLOWED_THUMB_TYPES.md
        )}`;
      break;

    case ALLOWED_FEATURE_KEYS.news:
      if (!_.isEmpty(data?.newsimage))
        returnVal = `${CDN_URL}${getThumbImageUrl(
          data?.newsimage,
          ALLOWED_THUMB_TYPES.sm
        )}`;

    default:
      break;
  }

  return returnVal;
};

export const shuffleComments = (key, arrComments = []) => {
  if (arrComments) {
    for (let i = 2; i < arrComments.length; i++) {
      if (arrComments[i].parentid) {
        const idx = _.findLastIndex(
          arrComments.slice(0, i),
          ea =>
            _.isEqual(ea[key], arrComments[i].parentid) ||
            _.isEqual(ea.parentid, arrComments[i].parentid)
        );
        if (idx >= 0) {
          const obj = arrComments.splice(i, 1)[0];
          arrComments.splice(idx + 1, 0, obj);
        }
      }
    }
  }
  return arrComments;
};

/**
 * @param type action type (social, gallery, session )
 * @returns database table name
 */
export const getEndPoint = type => {
  switch (type) {
    case ALLOWED_FEATURE_KEYS.social:
      return 'socialfeedcomments';
    case ALLOWED_FEATURE_KEYS.community:
      return 'communitypostcomment';
    case ALLOWED_FEATURE_KEYS.polls:
      return 'live_qa_reply';
    default:
      return 'comments';
  }
};

/**
 * Checks whether the data is of valid length or not
 * @param data
 */
export const isHavingContent = data => {
  if (typeof data === 'string') {
    data = data.trim();
  }
  return data ? data.length > 0 : false;
};

/**
 * File type icon
 * @param filepath
 * @returns
 */
export const fileImageType = filepath => {
  const extension = getExtension(filepath)?.toUpperCase();
  switch (extension) {
    case 'PDF':
      return assets.pdf;

    case 'PPT':
    case 'PPTX':
      return assets.ppt;

    case 'DOCX':
    case 'DOC':
      return assets.doc;

    case 'JPG':
    case 'JPEG':
    case 'PNG':
    case 'BMP':
    case 'SVG':
      return assets.img;

    case 'XLSX':
    case 'XLS':
      return assets.xls;

    default:
      return assets.doc;
  }
};

export const commonDateFNSFormatter = (date, fnsFormat) => {
  if (!isValidDate(date)) return '';

  const [mainRegion, subRegion] = _.split(LOCAL_TIMEZONE_LOCALE, '-');
  const tzLocal = _.join(
    [mainRegion, ...(!_.isEmpty(subRegion) ? _.toUpper(subRegion) : '')],
    ''
  );

  const languageKey =
    loadFromLocalDB(PORTAL_LOCALSTORAGE_ACCESS_KEY.state)?.session?.language ??
    INITIAL_APP_LANGUAGE;
  const isInitAppLang = _.isEqual(INITIAL_APP_LANGUAGE, languageKey);

  const locale =
    !isInitAppLang && languageKey && !_.isEmpty(Locales[languageKey])
      ? Locales[languageKey]
      : tzLocal && !_.isEmpty(Locales[tzLocal])
      ? Locales[tzLocal]
      : Locales.en; // Fallback to English

  return tzFormat(date, fnsFormat, {
    ...(!isInitAppLang ? { locale, timeZone: LOCAL_TIMEZONE } : {})
  });
};

export const LEAD_COLORS = [
  'black',
  'red',
  'orange',
  'aqua',
  'green',
  'blue',
  'silver',
  'maroon',
  'olive',
  'gray',
  'navy',
  'teal',
  'purple',
  'lightgray'
];

// Was unused at social component
const InstaLoader = () => {
  return (
    <div className="w-100 md:w-3/5 mx-auto p-4 text-center mb-4">
      <svg
        className="animate-spin h-8 w-8 mx-auto text-purple-700"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>
  );
};

export const getFullName = user => {
  const fName = user?.firstname ?? '';
  const lName = user?.lastname ?? '';

  return user?.username
    ? user?.username
    : `${fName}${!_.isEmpty(fName) && !_.isEmpty(lName) ? ' ' : ''}${lName}`;
};

export const getCompanyDesignation = (user, onlyWight = null) => {
  const position = user?.position ?? '';
  const company = user?.company ?? '';
  const bothPresent = !_.isEmpty(position) && !_.isEmpty(company);

  return (
    <>
      <span>
        {position}
        {!_.isEmpty(position) && !_.isEmpty(company)
          ? ` ${LocalStrings.dynamic.kAt} `
          : ''}
      </span>
      <span
        style={{
          ...(bothPresent && !onlyWight ? { color: 'var(--theme-color)' } : {}),
          ...(onlyWight ? { fontWeight: 800 } : {})
        }}
      >
        {company}
      </span>
    </>
  );
};

// return user job details
export const getJobTitle = user => {
  let jobTitle;
  let designation =
    user?.jobtitle ?? user?.jobtitile ?? user?.designation ?? '';
  let company = user?.institution ?? user?.company ?? '';

  if (!_.isEmpty(user?.responses)) {
    let user_responses = user?.responses;
    if (isJSONParsable(user?.responses))
      user_responses = JSON.parse(user?.responses);

    let filterCompany = user_responses.filter(item => {
      return item.name === 'company' && item.value && item.value !== '';
    });
    let filterDesignation = user_responses.filter(item => {
      return item.name === 'designation' && item.value && item.value !== '';
    });
    company = filterCompany.length > 0 ? filterCompany[0].value : '';
    designation =
      filterDesignation.length > 0 ? filterDesignation[0].value : '';
  }

  if (company !== '' && designation !== '') {
    jobTitle = (
      <>
        <span>{designation}</span>
        <span> {LocalStrings.dynamic.kAt} </span>
        <span style={{ color: 'var(--theme-color)' }}>{company}</span>
      </>
    );
  } else if (company !== '' && designation === '') {
    jobTitle = company;
  } else {
    jobTitle = designation;
  }

  return <>{jobTitle}</>;
};

export const getFormattedFileName = (
  session,
  fileName = '',
  type,
  extra = { ext: null, section: null, category: null }
) => {
  let uuid = uuidV4();
  uuid = _.split(uuid, '-').join('');

  // --- File name | First part
  const eventIdPart = `eventify/${session.eventid}`;

  // --- File name | space, special character removing - ext extraction and append
  fileName = fileName.replace(/ /g, '_');
  const ext =
    !_.isNull(extra.ext) && !_.isEmpty(extra.ext)
      ? extra?.ext
      : !_.isEmpty(fileName)
      ? `${fileName.split('.').pop()}`
      : 'notfound';
  const actualFileName = `${
    !_.isEmpty(fileName)
      ? fileName
          .split('.')
          .shift()
          .replace(/[^\w\s]/gi, '_') + '_'
      : ''
  }${uuid}UW${session.userid}.${ext}`;

  switch (type) {
    case ALLOWED_FEATURE_KEYS.notes:
      fileName = `${eventIdPart}/images/appusers/notes/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.community:
      fileName = `${eventIdPart}/images/community/comments/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.social:
      fileName = `${eventIdPart}/social/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.gallery:
      fileName = `${eventIdPart}/images/galleryimages/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.message:
      fileName = `${eventIdPart}/chatmedia/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.profile:
      fileName = `${eventIdPart}/images/appusers/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.speaker:
      fileName = `${eventIdPart}/images/speakers/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.schedule:
      // fileName = `${eventIdPart}/images/session/${actualFileName}`;
      fileName = `${eventIdPart}/files/sessions/${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.sponsor:
      fileName = `${eventIdPart}/images/sponsors/${
        extra.section === 'banner' ? 'banner/' : ''
      }${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.exhibitor:
      fileName = `${eventIdPart}/images/exhibitors/${
        extra.section === 'banner' ? 'banner/' : ''
      }${actualFileName}`;
      break;

    case 'dynamic':
      fileName = `${eventIdPart}/files/${extra.section}${
        !_.isEmpty(extra?.category) ? `/${extra.category}` : ''
      }/${actualFileName}`;
      break;

    case 'galleryVideoThumb':
      fileName = {
        filename: `${actualFileName}`,
        foldername: `${eventIdPart}/files/${extra.section}/galleryvideos`,
        subfoldername: `${extra.section}/galleryvideos/`,
        thumbname: `thumbnail_${actualFileName}`
      };
      break;

    case 'common-comments':
      const URI = `${
        _.isEqual(extra.section, ALLOWED_FEATURE_KEYS.social) ? '' : '/images'
      }/${
        _.isEqual(extra.section, ALLOWED_FEATURE_KEYS.gallery)
          ? 'galleryimages'
          : _.isEqual(extra.section, ALLOWED_FEATURE_KEYS.polls)
          ? 'session'
          : extra.section
      }/${
        _.isEqual(extra.section, ALLOWED_FEATURE_KEYS.community)
          ? 'comments/'
          : ''
      }`;
      fileName = `${eventIdPart}${URI}${actualFileName}`;
      break;

    case ALLOWED_FEATURE_KEYS.polls:
      fileName = `${eventIdPart}/images/others/${actualFileName}`;
      break;

    case 'others':
      fileName = `${eventIdPart}/images/others/${LEAD_FORM_AUDIO_PREFIX}${actualFileName}`;
      break;

    default:
      fileName = actualFileName;
      break;
  }

  return fileName;
};

export const convertDateIntoLocal = date => {
  let stillUtc = new Date(date);
  let local = utcToZonedTime(stillUtc, LOCAL_TIMEZONE);

  return local;
};

export const detectBrowser = () => {
  let browserName = navigator.appName;
  let fullVersion = '' + parseFloat(navigator.appVersion);
  let nAgt = navigator.userAgent;
  let nameOffset, verOffset;

  if ((verOffset = nAgt.indexOf('OPR/')) != -1) {
    browserName = 'Opera';
    fullVersion = nAgt.substring(verOffset + 4);
  }
  // In older Opera, the true version is after "Opera" or after "Version"
  else if ((verOffset = nAgt.indexOf('Opera')) != -1) {
    browserName = 'Opera';
    fullVersion = nAgt.substring(verOffset + 6);
    if ((verOffset = nAgt.indexOf('Version')) != -1)
      fullVersion = nAgt.substring(verOffset + 8);
  }
  // In MSIE, the true version is after "MSIE" in userAgent
  else if ((verOffset = nAgt.indexOf('MSIE')) != -1) {
    browserName = 'Microsoft Internet Explorer';
    fullVersion = nAgt.substring(verOffset + 5);
  }
  // In Chrome, the true version is after "Chrome"
  else if ((verOffset = nAgt.indexOf('Chrome')) != -1) {
    browserName = 'Chrome';
    fullVersion = nAgt.substring(verOffset + 7);
  }
  // In Safari, the true version is after "Safari" or after "Version"
  else if ((verOffset = nAgt.indexOf('Safari')) != -1) {
    browserName = 'Safari';
    fullVersion = nAgt.substring(verOffset + 7);
    if ((verOffset = nAgt.indexOf('Version')) != -1)
      fullVersion = nAgt.substring(verOffset + 8);
  }
  // In Firefox, the true version is after "Firefox"
  else if ((verOffset = nAgt.indexOf('Firefox')) != -1) {
    browserName = 'Firefox';
    fullVersion = nAgt.substring(verOffset + 8);
  }
  // In most other browsers, "name/version" is at the end of userAgent
  else if (
    (nameOffset = nAgt.lastIndexOf(' ') + 1) <
    (verOffset = nAgt.lastIndexOf('/'))
  ) {
    browserName = nAgt.substring(nameOffset, verOffset);
    fullVersion = nAgt.substring(verOffset + 1);
    if (browserName.toLowerCase() == browserName.toUpperCase()) {
      browserName = navigator.appName;
    }
  }

  return { browserName, fullVersion };
};

// ----- Converts \n text to <br /> tag if found in param string value
export const nl2br = (str, is_xhtml) => {
  if (typeof str === 'undefined' || str === null) {
    return '';
  }
  let breakTag =
    is_xhtml || typeof is_xhtml === 'undefined' ? '<br />' : '<br>';
  return (str + '').replace(
    /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g,
    '$1' + breakTag + '$2'
  );
};

export const getFileAcceptType = filetype => {
  switch (filetype) {
    case 'image':
      return 'image/*';

    case 'video':
      return 'video/*';

    case 'audio':
      return 'audio/*';

    case 'document':
    case 'file':
      return 'application/*';

    default:
      // return 'application/pdf,application/vnd.ms-excel,application/msword,application/zip'
      return '';
  }
};

// If thumbPercentage is empty/null, will generate actual image
export const getThumbImageUrl = (urlPath = '', thumbPercentage = 'md') => {
  if (_.isEmpty(thumbPercentage)) return urlPath;

  let pathElements = urlPath?.split('/');
  if (pathElements?.length) {
    pathElements[pathElements?.length - 1] = `thumb${
      ALLOWED_THUMB_TYPES[thumbPercentage]
    }_${pathElements[pathElements.length - 1]}`;
  }
  return pathElements?.join('/');
};

export const getUserRolePrivilege = (
  userDetails,
  eventTabs,
  userAccessByRole,
  type
) => {
  let filterData = null;
  // let adminPrivilege = userDetails?.isadmin ?? false;
  let adminPrivilege = false;
  let rolePrivilege = {};

  if (!_.includes(ROLE_PRIVILEGE_CHECKING_CASES, type))
    return { type: adminPrivilege };

  const accessedFeatures = userAccessByRole?.features;

  switch (type) {
    case ALLOWED_FEATURE_KEYS.community:
      rolePrivilege = {};

      _.map(ALLOWED_COMMUNITY_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        //   if (redirectType === ALLOWED_FEATURE_KEYS.community) {
        //     rolePrivilege.create_job = filterData?.isenabled ?? false;
        //     rolePrivilege.create_meetup = filterData?.isenabled ?? false;
        //   }
        // } else {
        let redirectPermission = false;
        if (!_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions => permissions?.tabslected === redirectType
              );

              if (redirectType === ALLOWED_FEATURE_KEYS.community) {
                if (!_.isEmpty(redirectTypePermission?.permissions)) {
                  rolePrivilege[redirectType] =
                    redirectTypePermission?.permissions?.moderate_community;
                  rolePrivilege.create_meetup =
                    redirectTypePermission?.permissions?.createmeetups_community;
                  rolePrivilege.create_job =
                    redirectTypePermission?.permissions?.createjobpost_community;
                } else {
                  rolePrivilege[redirectType] = redirectPermission;
                  rolePrivilege.create_meetup = redirectPermission;
                  rolePrivilege.create_job = redirectPermission;
                }
              } else {
                rolePrivilege[redirectType] = !_.isEmpty(
                  redirectTypePermission
                );
              }
            } else {
              rolePrivilege[redirectType] = filterData?.isenabled;
              rolePrivilege.create_meetup = filterData?.isenabled;
              rolePrivilege.create_job = filterData?.isenabled;
            }
          }
        } else {
          rolePrivilege[redirectType] = redirectPermission;
          rolePrivilege.create_meetup = redirectPermission;
          rolePrivilege.create_job = redirectPermission;
        }
        // }
      });
      break;

    case ALLOWED_FEATURE_KEYS.gallery:
      rolePrivilege = {};

      _.map(ALLOWED_GALLERY_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        //   if (redirectType === ALLOWED_FEATURE_KEYS.gallery) {
        //     rolePrivilege.addgallery = filterData?.isenabled ?? false;
        //     rolePrivilege.deleteGallery = filterData?.isenabled ?? false;
        //   }
        // } else {
        let redirectPermission = false;
        if (!_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions => permissions?.tabslected === redirectType
              );

              if (redirectType === ALLOWED_FEATURE_KEYS.gallery) {
                if (!_.isEmpty(redirectTypePermission?.permissions)) {
                  rolePrivilege[redirectType] = filterData?.isenabled;
                  rolePrivilege.addgallery =
                    redirectTypePermission?.permissions?.add_gallery;
                  rolePrivilege.deleteGallery =
                    redirectTypePermission?.permissions?.moderate_gallery;
                } else {
                  rolePrivilege[redirectType] = filterData?.isenabled;
                  rolePrivilege.addgallery = redirectPermission;
                  rolePrivilege.deleteGallery = redirectPermission;
                }
              } else {
                rolePrivilege[redirectType] = !_.isEmpty(
                  redirectTypePermission
                );
              }
            } else {
              rolePrivilege.addgallery = filterData?.isenabled;
              rolePrivilege.deleteGallery = filterData?.isenabled;
              rolePrivilege[redirectType] = filterData?.isenabled;
            }
          }
        } else {
          rolePrivilege[redirectType] = redirectPermission;
          rolePrivilege.addgallery = redirectPermission;
          rolePrivilege.deleteGallery = redirectPermission;
        }
        // }
      });
      break;

    case ALLOWED_FEATURE_KEYS.schedule:
      rolePrivilege = {};

      _.map(ALLOWED_SESSION_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab =>
            tab?.tabslected ===
              (_.includes(
                [ALLOWED_FEATURE_KEYS.polls, 'videoStream'],
                redirectType
              )
                ? ALLOWED_FEATURE_KEYS.schedule
                : redirectType) && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        // } else {
        let redirectPermission = false;
        if (!_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions =>
                  permissions?.tabslected ===
                  (_.includes(
                    [ALLOWED_FEATURE_KEYS.polls, 'videoStream'],
                    redirectType
                  )
                    ? ALLOWED_FEATURE_KEYS.schedule
                    : redirectType)
              );

              if (
                redirectType === 'videoStream' &&
                !_.isEmpty(redirectTypePermission?.permissions)
              ) {
                redirectPermission =
                  redirectTypePermission?.permissions?.sessionlivestream;
              } else if (
                redirectType === ALLOWED_FEATURE_KEYS.polls &&
                !_.isEmpty(redirectTypePermission?.permissions)
              ) {
                redirectPermission =
                  redirectTypePermission?.permissions?.sessionpollandliveqa;
              } else redirectPermission = !_.isEmpty(redirectTypePermission);
            } else {
              redirectPermission = filterData?.isenabled;
            }
          }
        }

        rolePrivilege[redirectType] = redirectPermission;
        // }
      });

      break;

    case ALLOWED_FEATURE_KEYS.social:
      rolePrivilege = {};
      _.map(ALLOWED_SOCIAL_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        // } else {
        let redirectPermission = false;
        if (!_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions => permissions?.tabslected === redirectType
              );

              if (redirectType === ALLOWED_FEATURE_KEYS.social) {
                if (!_.isEmpty(redirectTypePermission?.permissions)) {
                  rolePrivilege[redirectType] = filterData?.isenabled;
                  rolePrivilege.addSocial =
                    redirectTypePermission?.permissions?.add_social ?? true;
                  rolePrivilege.deleteSocial =
                    redirectTypePermission?.permissions?.moderate_social;
                } else {
                  rolePrivilege[redirectType] = filterData?.isenabled;
                  rolePrivilege.addSocial = redirectPermission;
                  rolePrivilege.deleteSocial = redirectPermission;
                }
              } else {
                rolePrivilege[redirectType] = !_.isEmpty(
                  redirectTypePermission
                );
              }
            } else {
              rolePrivilege.addSocial = filterData?.isenabled;
              rolePrivilege.deleteSocial = filterData?.isenabled;
              rolePrivilege[redirectType] = filterData?.isenabled;
            }
          }
        } else {
          rolePrivilege[redirectType] = redirectPermission;
          rolePrivilege.addSocial = redirectPermission;
          rolePrivilege.deleteSocial = redirectPermission;
        }
        // }
      });

      break;

    case ALLOWED_FEATURE_KEYS.scans:
      rolePrivilege = {};

      _.map(ALLOWED_MYSCAN_REDIRECTIONS, redirectType => {
        if (redirectType === 'qr') {
          const qrFeature = _.find(
            eventTabs,
            tab =>
              tab?.tabslected === ALLOWED_FEATURE_KEYS.scans &&
              Number(tab?.taborder) === 100 &&
              tab?.isenabled
          );

          rolePrivilege.qr = !_.isEmpty(qrFeature);

          if (!_.isEmpty(qrFeature)) {
            if (userAccessByRole?.version && userAccessByRole?.version >= 2) {
              rolePrivilege.qr = accessedFeatures?.qr ? true : false;
            }
          }
        } else if (redirectType === 'scanning') {
          rolePrivilege.allow_scan = true;

          const userIsBoothPersonnel =
            !_.isNil(userDetails?.exhibitoridfk) ||
            !_.isEmpty(userDetails?.exhibitoridfk);

          rolePrivilege.show_retrieve = userIsBoothPersonnel;

          if (!_.isEmpty(accessedFeatures)) {
            let scanPermission = _.find(
              accessedFeatures,
              (permissions, permissionKey) => permissionKey === redirectType
            );

            if (_.isEmpty(scanPermission)) {
              rolePrivilege.show_retrieve = userIsBoothPersonnel;
            } else {
              if (!_.isEmpty(scanPermission?.permissions)) {
                rolePrivilege.show_retrieve =
                  scanPermission?.permissions?.lead_qualification &&
                  userIsBoothPersonnel;
              }
            }
          }
        } else {
          filterData = _.find(
            eventTabs,
            tab => tab?.tabslected === redirectType && tab?.isenabled
          );

          // if (userDetails?.isadmin) {
          //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
          // } else {
          let redirectPermission = false;
          if (!_.isEmpty(eventTabs)) {
            if (filterData) {
              if (!_.isEmpty(accessedFeatures)) {
                let redirectTypePermission = _.find(
                  accessedFeatures,
                  permissions => permissions?.tabslected === redirectType
                );

                redirectPermission = !_.isEmpty(redirectTypePermission);
              } else {
                redirectPermission = filterData?.isenabled;
              }
            }
          }

          rolePrivilege[redirectType] = redirectPermission;
          // }
        }
      });
      break;

    case ALLOWED_FEATURE_KEYS.notifications:
      rolePrivilege = {};

      _.map(ALLOWED_NOTIFICATION_REDIRECTIONS, redirectType => {
        let redirectPermission = redirectType === 'external';

        if (!redirectPermission && !_.isEmpty(eventTabs)) {
          filterData = _.find(
            eventTabs,
            tab =>
              tab?.tabslected ===
                (redirectType === 'session' || redirectType === 'poll'
                  ? 'schedule'
                  : redirectType) && tab?.isenabled
          );

          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions =>
                  permissions?.tabslected ===
                  (redirectType === 'poll' || redirectType === 'session'
                    ? 'schedule'
                    : redirectType)
              );

              if (
                redirectType === 'session' &&
                _.isEmpty(redirectTypePermission)
              ) {
                // redirectPermission = userDetails?.isadmin ?? false;
                redirectPermission = false;
              } else if (
                redirectType === 'poll' &&
                !_.isEmpty(redirectTypePermission?.permissions)
              ) {
                redirectPermission =
                  redirectTypePermission?.permissions?.sessionpollandliveqa;
              } else if (_.isEmpty(redirectTypePermission)) {
                // redirectPermission = userDetails?.isadmin ?? false;
                redirectPermission = false;
              } else redirectPermission = true;
            } else {
              redirectPermission = filterData?.isenabled;
            }
          }
        }

        rolePrivilege[redirectType] = redirectPermission;
      });

      break;

    case ALLOWED_FEATURE_KEYS.attendee:
    case ALLOWED_FEATURE_KEYS.exhibitor:
    case ALLOWED_FEATURE_KEYS.sponsor:
    case ALLOWED_FEATURE_KEYS.message:
    case ALLOWED_FEATURE_KEYS.meetings:
    case ALLOWED_FEATURE_KEYS.gamification:
      rolePrivilege = {};

      _.map(ALLOWED_ATTENDEE_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        if (redirectType === ALLOWED_FEATURE_KEYS.attendee) {
          rolePrivilege[redirectType] = true;
        }
        // else if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;

        //   if (redirectType === ALLOWED_FEATURE_KEYS.meetings) {
        //     if (!_.isEmpty(userAccessByRole)) {
        //       let meetingPermission = _.find(
        //         userAccessByRole,
        //         permissions => permissions?.tabslected === redirectType
        //       );

        //       if (!_.isEmpty(meetingPermission)) {
        //         rolePrivilege.can_initiate_meeting =
        //           meetingPermission?.can_initiate ?? true;
        //       } else rolePrivilege.can_initiate_meeting = true;
        //     } else rolePrivilege.can_initiate_meeting = true;
        //   }
        // }
        else {
          let redirectPermission = false;
          if (!_.isEmpty(eventTabs)) {
            if (filterData) {
              if (!_.isEmpty(accessedFeatures)) {
                let redirectTypePermission = _.find(
                  accessedFeatures,
                  permissions => permissions?.tabslected === redirectType
                );

                redirectPermission = !_.isEmpty(redirectTypePermission);

                if (redirectType === ALLOWED_FEATURE_KEYS.meetings) {
                  if (!_.isEmpty(redirectTypePermission)) {
                    rolePrivilege.can_initiate_meeting =
                      redirectTypePermission?.can_initiate ?? true;
                  } else rolePrivilege.can_initiate_meeting = true;
                }
              } else {
                redirectPermission = filterData?.isenabled;
                if (redirectType === ALLOWED_FEATURE_KEYS.meetings)
                  rolePrivilege.can_initiate_meeting = true;
              }
            }
          }
          rolePrivilege[redirectType] = redirectPermission;
        }
      });

      break;

    case ALLOWED_FEATURE_KEYS.speaker:
      rolePrivilege = {};

      _.map(ALLOWED_SPEAKER_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        // } else {
        let redirectPermission = false;

        if (!_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions => permissions?.tabslected === redirectType
              );

              redirectPermission = !_.isEmpty(redirectTypePermission);
            } else {
              redirectPermission = filterData?.isenabled;
            }
          }
        }

        rolePrivilege[redirectType] = redirectPermission;
        // }
      });

      break;

    case ALLOWED_FEATURE_KEYS.maps:
      rolePrivilege = {};

      _.map(ALLOWED_MAPS_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        // } else {
        let redirectPermission = false;

        if (!_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions => permissions?.tabslected === redirectType
              );

              redirectPermission = !_.isEmpty(redirectTypePermission);
            } else {
              redirectPermission = filterData?.isenabled;
            }
          }
        }

        rolePrivilege[redirectType] = redirectPermission;
        // }
      });

      break;

    case ALLOWED_FEATURE_KEYS.globalsearch:
      rolePrivilege = {};

      _.map(ALLOWED_GLOBALSEARCH_REDIRECTIONS, redirectType => {
        if (redirectType === ALLOWED_FEATURE_KEYS.notes) {
          rolePrivilege[redirectType] = true;
        } else {
          filterData = _.find(
            eventTabs,
            tab => tab?.tabslected === redirectType && tab?.isenabled
          );

          // if (userDetails?.isadmin) {
          //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
          // } else {
          let redirectPermission = false;
          if (!_.isEmpty(eventTabs)) {
            if (filterData) {
              if (!_.isEmpty(accessedFeatures)) {
                let redirectTypePermission = _.find(
                  accessedFeatures,
                  permissions => permissions?.tabslected === redirectType
                );

                redirectPermission = !_.isEmpty(redirectTypePermission);
              } else {
                redirectPermission = filterData?.isenabled;
              }
            }

            rolePrivilege[redirectType] = redirectPermission;
          }
          // }
        }
      });

      break;

    case ALLOWED_FEATURE_KEYS.bookmark:
      rolePrivilege = {};
      _.map(ALLOWED_BOOKMARK_REDIRECTIONS, redirectType => {
        if (redirectType === ALLOWED_FEATURE_KEYS.notes) {
          rolePrivilege[redirectType] = true;
        } else {
          filterData = _.find(
            eventTabs,
            tab => tab?.tabslected === redirectType && tab?.isenabled
          );

          // if (userDetails?.isadmin) {
          //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
          // } else {
          let redirectPermission = false;

          if (!redirectPermission && !_.isEmpty(eventTabs)) {
            if (filterData) {
              if (!_.isEmpty(accessedFeatures)) {
                let redirectTypePermission = _.find(
                  accessedFeatures,
                  permissions => permissions?.tabslected === redirectType
                );

                redirectPermission = !_.isEmpty(redirectTypePermission);
              } else {
                redirectPermission = filterData?.isenabled;
              }
            }
          }
          rolePrivilege[redirectType] = redirectPermission;
          // }
        }
      });

      break;

    case ALLOWED_FEATURE_KEYS.notes:
      rolePrivilege = {};

      _.map(ALLOWED_NOTES_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        // } else {
        let redirectPermission = false;

        if (!redirectPermission && !_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions => permissions?.tabslected === redirectType
              );

              redirectPermission = !_.isEmpty(redirectTypePermission);
            } else {
              redirectPermission = filterData?.isenabled;
            }
          }
        }

        rolePrivilege[redirectType] = redirectPermission;
        // }
      });

      break;

    case ROLE_PRIVILEGE_SPECIAL_CASES.ad:
      rolePrivilege = {};

      _.map(ALLOWED_ADVERTISEMENT_REDIRECTIONS, redirectType => {
        filterData = _.find(
          eventTabs,
          tab => tab?.tabslected === redirectType && tab?.isenabled
        );

        // if (userDetails?.isadmin) {
        //   rolePrivilege[redirectType] = filterData?.isenabled ?? false;
        // } else {
        let redirectPermission = false;
        if (!_.isEmpty(eventTabs)) {
          if (filterData) {
            if (!_.isEmpty(accessedFeatures)) {
              let redirectTypePermission = _.find(
                accessedFeatures,
                permissions => permissions?.tabslected === redirectType
              );

              redirectPermission = !_.isEmpty(redirectTypePermission);
            } else {
              redirectPermission = filterData?.isenabled;
            }
          }
        }
        rolePrivilege[redirectType] = redirectPermission;
        // }
      });

      break;

    default:
      rolePrivilege = { type: adminPrivilege };
      break;
  }

  return rolePrivilege;
};

export const getAppTabName = (
  eventTabs,
  appFeature = '',
  checkEnable = false
) => {
  if (
    _.includes(MULTI_TAB_FEATURES, appFeature) &&
    _.isEqual(appFeature, ALLOWED_FEATURE_KEYS.webview) // currently restricted to webview only
  ) {
    let foundFeatureDisplayName = _.filter(
      eventTabs,
      feature =>
        _.isEqual(feature?.tabslected, appFeature) &&
        (checkEnable ? feature?.isenabled : true)
    );

    let filteredNames = [];
    _.map(foundFeatureDisplayName, tab => {
      filteredNames.push({ id: tab?.apptabid, name: tab?.displaytabname });
    });
    return filteredNames;
  } else {
    let foundFeatureDisplayName = _.find(
      eventTabs,
      feature =>
        _.isEqual(feature?.tabslected, appFeature) &&
        (checkEnable ? feature?.isenabled : true)
    );

    return foundFeatureDisplayName?.displaytabname ?? null;
  }
};

export const getAppTabId = (
  eventTabs,
  appFeature = '',
  checkEnable = false
) => {
  if (
    _.includes(MULTI_TAB_FEATURES, appFeature) &&
    _.isEqual(appFeature, ALLOWED_FEATURE_KEYS.webview)
  ) {
    let foundFeatureDisplayName = _.filter(
      eventTabs,
      feature =>
        _.isEqual(feature.tabslected, appFeature) &&
        (checkEnable ? feature?.isenabled : true)
    );

    let filteredIds = [];
    _.map(foundFeatureDisplayName, tab => {
      filteredIds.push(tab?.apptabid);
    });
    return filteredIds;
  } else {
    let foundFeatureDisplayName = _.find(
      eventTabs,
      feature =>
        _.isEqual(feature.tabslected, appFeature) &&
        (checkEnable ? feature?.isenabled : true)
    );
    return foundFeatureDisplayName?.apptabid ?? null;
  }
};

export const checkFeatureAllowed = (
  session,
  eventSettings,
  eventTabs,
  userAccessByRole,
  appTabId = 0
) => {
  const isBeforeLogin = _.isEmpty(session?.sessionToken);

  if (eventSettings?.isloginmandatory && isBeforeLogin) return false;

  const featureAppTabDetails = _.find(
    eventTabs,
    tab => _.isEqual(tab?.apptabid, appTabId) && tab?.isenabled
  );
  if (_.isEmpty(featureAppTabDetails)) return false;

  if (!isBeforeLogin) {
    if (_.isEmpty(userAccessByRole?.features)) return true;
    else {
      let redirectTypePermission = _.find(
        userAccessByRole?.features,
        permissions => _.isEqual(permissions?.apptabid, appTabId)
      );

      return !_.isEmpty(redirectTypePermission);
    }
  }

  return true;
};

export const handleTrickyBoolean = value => {
  if (typeof value === 'string') return JSON.parse(value);
  return value;
};

export const getSectionListingSortType = (eventSettings, appFeature) => {
  let sortType = 'az';

  switch (appFeature) {
    case ALLOWED_FEATURE_KEYS.attendee:
      sortType = eventSettings?.attendeesort ?? 'az';
      break;
    case ALLOWED_FEATURE_KEYS.exhibitor:
      sortType = eventSettings?.exhibitorsort ?? 'az';
      break;
    case ALLOWED_FEATURE_KEYS.speaker:
      sortType = eventSettings?.speakersort ?? 'az';
      break;
    case ALLOWED_FEATURE_KEYS.sponsor:
      sortType = eventSettings?.sponsorsort ?? 'az';
      break;

    default:
      sortType = 'az';
      break;
  }

  return sortType;
};

export const getYoutubeIDFromURL = url => {
  const regex = /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm;
  return regex.exec(url)?.[3] ?? '';
};

export const getWebVideoThumbnail = async url => {
  if (url.includes('vimeo.')) {
    const res = await fetch(
      'https://vimeo.com/api/oembed.json?url=' + encodeURI(url)
    );
    const result =
      res.status >= 200 && res.status <= 400 ? await res.json() : undefined;
    return result?.thumbnail_url;
  } else if (url.includes('youtu.be') || url.includes('youtube.')) {
    // It is a youtube link
    const results = getYoutubeIDFromURL(url);
    if (results.length > 0) {
      return 'http://img.youtube.com/vi/' + results + '/0.jpg';
    }
  }
  return null;
};

export async function downloadFileFromURL(url) {
  const a = document.createElement('a');
  a.href = `${CDN_URL}${url}?download=true`;
  a.download = getFileNameFromPath(url);
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(urlBlob);
}

export const getUploadBaseURL = session => {
  const url = IS_API4
    ? session.instance_url
    : replaceAll(
        session.instance_url,
        `.${PROJECT_URI}`,
        `-files.${PROJECT_URI}`
      );

  return url;
};

export const parseDbUtcIntoDateObj = (dateString = '') => {
  const [datePart, timePart] = dateString.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
};

export const parseGivenDateIntoUtcWithFormat = (
  time = '',
  formatString = ''
) => {
  const date = new Date(time);
  const zonedDate = utcToZonedTime(date, 'UTC');
  const formattedDate = tzFormat(zonedDate, formatString, {
    timeZone: 'UTC'
  });
  return formattedDate;
};

export const parseUtcFormattedStringToLocalDate = (
  formattedDateString = '',
  formatString = ''
) => {
  const utcDate = parse(formattedDateString, formatString, new Date());
  const localDate = zonedTimeToUtc(utcDate, 'UTC');
  return localDate;
};

export const getRegistrationLoginOpen = eventSettings => {
  const returnRes = {
    registration: true,
    login: true
  };
  const regLoginResDates = eventSettings?.reg_login_dates;

  if (_.isEmpty(regLoginResDates)) return returnRes;

  let regStart = regLoginResDates?.registrationdates?.startdate;
  let regEnd = regLoginResDates?.registrationdates?.enddate;
  let loginStart = regLoginResDates?.logindates?.startdate;
  let loginEnd = regLoginResDates?.logindates?.enddate;

  if (isValidDate(regStart) && isValidDate(regEnd)) {
    regStart = new Date(`${regStart} 00:00:00`);
    regEnd = new Date(`${regEnd} 00:00:00`);

    if (isBefore(new Date(), regStart) || isAfter(new Date(), regEnd))
      returnRes.registration = false;
  }

  if (isValidDate(loginStart) && isValidDate(loginEnd)) {
    loginStart = new Date(`${loginStart} 00:00:00`);
    loginEnd = new Date(`${loginEnd} 00:00:00`);

    if (isBefore(new Date(), loginStart) || isAfter(new Date(), loginEnd))
      returnRes.login = false;
  }

  return returnRes;
};

// Manages the base url for custom domain internal redirects
export const getSameDomainBaseUrl = session => {
  const applySiteUrl = !(
    !_.includes(['0.0.0.0', 'localhost'], window.location.hostname) &&
    !_.includes(window.location.hostname, session?.site_url)
  );

  return `https://${session?.seocode}${
    applySiteUrl ? `.${session?.site_url}` : ''
  }`;
};

export const handleRankText = position => {
  const rank = typeof position == 'string' ? _.toInteger(position) : position;

  let suffix = 'th';
  if (rank % 100 < 11 || rank % 100 > 13) {
    switch (rank % 10) {
      case 1:
        suffix = 'st';
        break;
      case 2:
        suffix = 'nd';
        break;
      case 3:
        suffix = 'rd';
        break;
    }
  }

  return rank + suffix;
};

export const getRecommendedFeaturedByType = (eventSettings, type) => {
  const overViewSettings = eventSettings?.overview_settings;
  if (_.isEmpty(overViewSettings)) return ALLOWED_FEATURED_TYPES.none;

  switch (type) {
    case ALLOWED_FEATURE_KEYS.speaker:
      return overViewSettings?.viewtype_speaker;

    case ALLOWED_FEATURE_KEYS.exhibitor:
      return overViewSettings?.viewtype_exhibitor;

    case ALLOWED_FEATURE_KEYS.news:
      return overViewSettings?.viewtype_news;

    default:
      return ALLOWED_FEATURED_TYPES.none;
  }
};

export const getEventThemeColors = (session, eventDetails) => {
  const themeColor = {
    light: eventDetails?.themecolor,
    dark: eventDetails?.darkthemecolor,
    selected: _.isEqual(ALLOWED_APP_THEMES.dark, session?.theme)
      ? eventDetails?.darkthemecolor
      : eventDetails?.themecolor
  };

  return themeColor;
};

/**
 * @param pattern string | string[]
 * @param replacement sting | string[]
 */
export const replaceAll = (string, pattern, replacement) => {
  if (_.isEmpty(string) || _.isEmpty(pattern) || _.isNull(replacement))
    return string;

  if (typeof replacement === 'number') replacement = String(replacement);

  if (typeof pattern === 'string' && typeof replacement === 'string') {
    const regex = new RegExp(pattern, 'g');
    return _.replace(string, regex, replacement);
  }

  if (
    typeof pattern === 'object' &&
    typeof replacement === 'object' &&
    _.isEqual(pattern.length, replacement.length)
  ) {
    let returnStr = string;
    _.times(pattern.length, i => {
      const regex = new RegExp(pattern[i], 'g');
      returnStr = _.replace(returnStr, regex, replacement[i]);
    });
    return returnStr;
  }

  return string;
};

export const compareTimeZones = (local, db) => {
  if (_.isEqual(local, db)) return true;

  let localTZ = local;
  switch (local) {
    case 'Asia/Calcutta':
      localTZ = 'Asia/Kolkata';
      break;
    case 'Africa/Harare':
      localTZ = 'Africa/Johannesburg';
      break;
    case 'Asia/Istanbul':
      localTZ = 'Europe/Istanbul';
      break;
    case 'America/Buenos_Aires':
      localTZ = 'America/Argentina/Buenos_Aires';
      break;
    case 'Australia/Sydney':
      localTZ = 'Australia/NSW';
      break;

    default:
      localTZ = local;
      break;
  }

  return localTZ === db;
};

export const checkSessionStartIfSameInUTC = (startTimeUTC, scheduleDate) => {
  const startTimeInZone = utcToZonedTime(startTimeUTC, LOCAL_TIMEZONE);
  return isSameDay(startTimeInZone, scheduleDate) ? false : true;
};

export const setScheduleInLocalStorage = (
  eventData = null, // should be passed for initial set only
  eventSettings = {},
  timeExtra = {
    wantsConverted: null,
    userUpdatedTZ: null
  },
  viewExtra = {
    view: '',
    userUpdatedView: null
  }
) => {
  const tz = LOCAL_TIMEZONE; // Client/system time-zone

  let payload = !_.isEmpty(eventData)
    ? _.cloneDeep(SCHEDULE_LOCALSTORAGE_STRUCTURE)
    : loadFromLocalDB(PORTAL_LOCALSTORAGE_ACCESS_KEY.schedule);

  if (!_.isEmpty(eventData)) {
    payload.userWantsConvertedTime = !eventData?.showconvertedtimezone;
    payload.userInEventTimeZone = compareTimeZones(
      tz,
      eventData?.eventtimezone
    );
    payload.userUpdatedTZ = false;

    const eventSelectedSessionView = eventSettings?.scheduleview; // what view is selected from admin-panel
    payload.view =
      !_.isEmpty(eventSelectedSessionView) &&
      _.includes(ALLOWED_SCHEDULE_VIEWS.types, eventSelectedSessionView)
        ? ALLOWED_SCHEDULE_VIEWS[eventSelectedSessionView]
        : ALLOWED_SCHEDULE_VIEWS.grid;
    payload.userUpdatedView = false;
  } else if (!_.isEmpty(timeExtra)) {
    payload.userWantsConvertedTime = timeExtra?.wantsConverted;
    payload.userUpdatedTZ = true;
  } else if (!_.isEmpty(viewExtra)) {
    payload.view = !_.isEmpty(viewExtra?.view)
      ? viewExtra?.view
      : ALLOWED_SCHEDULE_VIEWS.grid;
    payload.userUpdatedView = true;
  }

  setInLocalDB({
    key: PORTAL_LOCALSTORAGE_ACCESS_KEY.schedule,
    value: payload
  });
};

//method to identify OS
export const getOS = () => {
  const userAgent = window.navigator.userAgent;
  let platform =
    window.navigator?.userAgentData?.platform ||
    window.navigator.platform ||
    'unknown';
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  const androidPlatform = /Android/.test(userAgent);
  const linuxPlatform = /Linux/.test(platform);

  if (macosPlatforms.includes(platform)) {
    return 'macos';
  } else if (iosPlatforms.includes(platform)) {
    return 'ios';
  } else if (windowsPlatforms.includes(platform)) {
    return 'windows';
  } else if (androidPlatform) {
    return 'android';
  } else if (linuxPlatform) {
    return 'linux';
  } else {
    return 'unknown';
  }
};

export const setSmartBanners = data => {
  if (['ios', 'android'].includes(getOS())) {
    function getAppStoreID(url) {
      const regex = /\/id(\d+)/;
      const match = url.match(regex);
      return match ? match[1] : null;
    }

    function getGoogleBundleId(url) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      return urlParams?.get('id');
    }

    const appStoreID = getAppStoreID(data.iosappstorelink);
    const playStoreBundleID = getGoogleBundleId(data.androidplaystorelink);

    function updateMetaTag(name, content) {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = name;
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    }

    function updateLinkTag(rel, href) {
      let linkTag = document.querySelector(`link[rel="${rel}"]`);
      if (!linkTag) {
        linkTag = document.createElement('link');
        linkTag.rel = rel;
        document.head.appendChild(linkTag);
      }
      linkTag.href = href;
    }

    updateMetaTag('apple-itunes-app', `app-id=${appStoreID}`);
    updateMetaTag('google-play-app', `app-id=${playStoreBundleID}`);
    updateLinkTag('apple-touch-icon', `${CDN_URL + data.eventicon}`);
    updateLinkTag('android-touch-icon', `${CDN_URL + data.eventicon}`);
  }
};

/**
 * NOTE:
 * Picked up from profile component's form onSubmit validation with minor tweaks.
 * If core logic changes, change in respective sections' too.
 */
export const isMandatoryProfileFieldEmpty = (userDetails, eventForms) => {
  if (_.isEmpty(userDetails)) return false;

  const isSingle =
    userDetails?.layouts?.attendee === ALLOWED_FORM_LAYOUTS.single;

  const userInterest = !_.isEmpty(userDetails?.userinterests_by_appuserid)
    ? _.map(userDetails?.userinterests_by_appuserid, item => item.interestid)
    : [];

  const profileSections = isSingle
    ? userDetails?.personal_profile?.profile_details?.section_items
    : userDetails?.personal_profile;

  const profileImageRenderType =
    eventForms?.profileImage?.[ALLOWED_FEATURE_KEYS.attendee];

  const checkMandatoryFields = [];

  // - Profile image mandatory check based on attendee form settings
  if (
    _.isEqual(ALLOWED_FORM_PROFILE_IMAGE.mandatory, profileImageRenderType) &&
    _.isEmpty(userDetails?.profileurl)
  )
    checkMandatoryFields.push(
      `${LocalStrings.dynamic.kProfileText} ${LocalStrings.dynamic.kImageText}`
    );

  _.map(profileSections, section => {
    _.map(section?.section_items, secItem => {
      const itemReadyToTest =
        !section.hideForCondition &&
        !secItem.hidden &&
        secItem.isactive &&
        secItem.required &&
        !secItem.isreadonly &&
        !secItem.hideForCondition;

      const interestMissing =
        _.isEqual(secItem.key, 'dynamicfield') &&
        (_.isEqual(secItem.id, 'interest') ||
          _.isEqual(secItem.id, 'userinterests') ||
          _.isEqual(secItem.name, 'interest') ||
          _.isEqual(secItem.name, 'userinterests')) &&
        itemReadyToTest &&
        !userInterest.length;

      const normalFieldMissing =
        !_.isEqual(secItem?.key, 'singlecheckbox') &&
        !_.isEqual(secItem.key, 'dynamicfield') &&
        _.isEmpty(secItem.value) &&
        itemReadyToTest;

      const gdprMissing =
        _.isEqual(secItem?.key, 'singlecheckbox') &&
        _.isEqual(secItem?.id, 'isnetworking') &&
        !secItem?.value &&
        itemReadyToTest &&
        false;

      if (normalFieldMissing || interestMissing || gdprMissing) {
        checkMandatoryFields.push(`${section.label} > ${secItem.label}`);
      }
    });
  });

  return checkMandatoryFields.length ? true : false;
};

export const removeSpecialCharacters = (str, removeUnderScore = false) => {
  return removeUnderScore
    ? str.replace(/[^a-zA-Z0-9\s]/g, '') // Regular expression to match any character that is not a letter, number, or space
    : str.replace(/[^\w\s]/g, ''); // Regular expression to match any character that is not a letter, number, or underscore
};

export const isAlphabet = str => {
  const regex = /^[A-Za-z]+$/;
  return regex.test(str);
};

export const getConditionalCommonFeatures = eventSettings => {
  const eventCustomSettings = eventSettings?.custom_settings;

  return !_.isNull(eventCustomSettings) &&
    !_.isEmpty(eventCustomSettings?.user_feature)
    ? eventCustomSettings?.user_feature
    : {
        notes: true,
        bookmarks: true,
        notifications: true
      };
};

export const getPortalAppVersion = () => {
  return packageJSON?.version;
};

export const hexToRGBA = (hex, opacity) => {
  const bigint = parseInt(hex?.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const loadLocalImage = image => {
  const img = new window.Image();
  img.src = image;
  return img;
};

export const getTextWithTruncate = (text = '', limit = 0) => {
  if (_.isEmpty(text) || text.length <= limit) return text;
  else {
    return `${text.substring(0, limit - 3)}...`;
  }
};

export const determineToLoadSurvey = (eventTabs, userAccessByRole, userId) => {
  const surveyFeatureEnabled = !_.isEmpty(eventTabs)
    ? !_.isEmpty(
        _.find(
          eventTabs,
          tab =>
            _.isEqual(tab?.tabslected, ALLOWED_FEATURE_KEYS.survey) &&
            tab?.isenabled
        )
      )
    : false;
  const rolePermittedSurveyTabAvailable = surveyFeatureEnabled
    ? !_.isEmpty(userAccessByRole?.features)
      ? !_.isEmpty(
          _.find(userAccessByRole?.features, permissions =>
            _.isEqual(permissions?.tabslected, ALLOWED_FEATURE_KEYS.survey)
          )
        )
      : _.isEmpty(userAccessByRole?.features)
    : false;
  const loadSurvey =
    surveyFeatureEnabled && rolePermittedSurveyTabAvailable && !_.isNil(userId);

  return loadSurvey;
};

export const determineGameChallengeVisibility = eventSettings => {
  const eventCustomSettings = eventSettings?.custom_settings ?? {};
  return !_.isEmpty(eventCustomSettings)
    ? eventCustomSettings?.gamification_challenge_visibility
    : GAMIFICATION_CHALLENGE_VISIBILITY.single;
};

export const prepareGameChallenges = (
  eventTabs,
  eventSettings,
  gameData,
  extra = { challengeId: null, sessionId: null }
) => {
  const simpleChallenges = [
    'completeprofile',
    'openapp',
    'globalsearch',
    'trivia'
  ];
  const qrChallenges = [
    'scansessionqr',
    'scanexhibitorqr',
    'scansponsorqr',
    'scanspeakerqr',
    'scanuserqr'
  ];
  const challengeVisibility = determineGameChallengeVisibility(eventSettings);

  const getFeatureKey = challengetype => {
    const featureMap = {
      makeconnection: ALLOWED_FEATURE_KEYS.message,
      sendmessage: ALLOWED_FEATURE_KEYS.message,
      attendsession: ALLOWED_FEATURE_KEYS.schedule,
      speakers: ALLOWED_FEATURE_KEYS.speaker,
      exhibitors: ALLOWED_FEATURE_KEYS.exhibitor,
      exhibitor: ALLOWED_FEATURE_KEYS.exhibitor,
      sponsor: ALLOWED_FEATURE_KEYS.sponsor,
      news: ALLOWED_FEATURE_KEYS.news,
      social: ALLOWED_FEATURE_KEYS.social,
      community: ALLOWED_FEATURE_KEYS.community,
      conversationer: ALLOWED_FEATURE_KEYS.community,
      gallerycontest: ALLOWED_FEATURE_KEYS.gallery
    };
    return featureMap[challengetype] || '';
  };

  const isChallengeAllowed = obj => {
    if (
      _.isEqual(
        challengeVisibility,
        GAMIFICATION_CHALLENGE_VISIBILITY.recurring
      )
    )
      return Boolean(obj?.isrecurring);
    if (obj?.isrecurring) {
      return !_.isEqual(
        challengeVisibility,
        GAMIFICATION_CHALLENGE_VISIBILITY.single
      );
    }
    if (_.includes(simpleChallenges, obj?.challengetype)) return true;
    if (_.includes(qrChallenges, obj?.challengetype) && !obj?.isrecurring)
      return true;

    const key = getFeatureKey(obj?.challengetype);
    return (
      !_.isEmpty(key) &&
      _.some(
        eventTabs,
        item => _.isEqual(item?.tabslected, key) && item?.isenabled
      )
    );
  };

  const filterChallenges = () => {
    if (
      _.isEmpty(challengeVisibility) ||
      !_.includes(GAMIFICATION_CHALLENGE_VISIBILITY.types, challengeVisibility)
    ) {
      return _.filter(gameData, obj => isChallengeAllowed(obj));
    }

    switch (challengeVisibility) {
      case GAMIFICATION_CHALLENGE_VISIBILITY.recurring:
        return _.filter(gameData, obj => obj?.isrecurring);
      case GAMIFICATION_CHALLENGE_VISIBILITY.all:
        return [gameData];
      default:
        return _.filter(
          gameData,
          obj => !obj?.isrecurring || isChallengeAllowed(obj)
        );
    }
  };

  const challenges = !_.isEmpty(eventTabs)
    ? _.filter(gameData, isChallengeAllowed)
    : filterChallenges();

  if (!_.isEmpty(extra?.challengeId))
    return _.filter(challenges, ch => _.includes(extra?.challengeId, ch.id));
  if (extra?.sessionId) {
    return _.filter(challenges, ch =>
      _.isEqual(extra?.sessionId, ch?.sessionid)
    );
  }
  return challenges;
};

export const determineSessionQuizAvailable = eventSettings =>
  handleTrickyBoolean(eventSettings?.custom_settings?.session_quizzes);

export const getUserGroupIds = userDetails =>
  _.split(userDetails?.usercategory, ',').map(ea => Number(ea));

export const getErrorMessageForLoginErrorCode = errorCode => {
  let errMsg = '';
  let title = null;
  switch (errorCode) {
    case 1:
      errMsg = LocalStrings.dynamic.kEmailNotRegistered;
      break;
    case 2:
      errMsg = LocalStrings.dynamic.kWaitListMode;
      break;
    case 3:
      errMsg = LocalStrings.dynamic.kInActiveUserMsg;
      title = LocalStrings.dynamic.kInActiveUser;
      break;
    case 4:
      errMsg = LocalStrings.dynamic.kEventNotStarted;
      break;
    case 5:
      errMsg = LocalStrings.dynamic.kInvalidPasscode;
      break;

    default:
      errMsg = LocalStrings.dynamic.kInActiveUserMsg;
      break;
  }

  showError(errMsg, title);
  return {
    title,
    errMsg
  };
};

/**
 * @param responseJson => Response field obj[]
 */
export const maintainResponseStructure = responseJson =>
  _.map(responseJson, field => {
    if (
      _.includes(['section', 'paragraph', 'label', 'hyperlink'], field?.key) &&
      _.isEmpty(field?.value)
    ) {
      delete field.value; // Remove 'value' if conditions match
    }
    return field;
  });

/**
 * @returns date in corresponding timezone.
 */
export const dateInTimezone = (dt = new Date(), timeZone = '') => {
  try {
    return Intl.DateTimeFormat('default', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit'
    }).format(dt);
  } catch {
    return Common.dateInTimezone(dt);
  }
};

export const getQRUrl = (session, link) => {
  if (_.isEmpty(session?.instance_url) || _.isEmpty(link)) return link;

  return replaceAll(
    SCAN_GENERATE_URL,
    ['###BASE_URL###', '###LINK_URL###'],
    [session?.instance_url, String(link)]
  );
};

/**
 * @returns section embed url
 */
export const generateSectionEmbedURL = (session, section) => {
  const languageKey =
    loadFromLocalDB(PORTAL_LOCALSTORAGE_ACCESS_KEY.state)?.session?.language ??
    INITIAL_APP_LANGUAGE;

  const SECTION_URLS = {};
  SECTION_URLS[ALLOWED_FEATURE_KEYS.schedule] = 'sessions';
  SECTION_URLS[ALLOWED_FEATURE_KEYS.speaker] = 'speakers';
  SECTION_URLS[ALLOWED_FEATURE_KEYS.sponsor] = 'sponsors';
  SECTION_URLS[ALLOWED_FEATURE_KEYS.exhibitor] = 'exhibitors';

  return replaceAll(
    WIDGET_URL,
    [
      '###BASE_URL###',
      '###SECTION###',
      '###BG_COLOR###',
      '###FONT_COLOR###',
      '###LANG_KEY###'
    ],
    [
      getSameDomainBaseUrl(session),
      SECTION_URLS[section],
      _.isEqual(session?.theme, ALLOWED_APP_THEMES.dark)
        ? '0x0F1527'
        : '0xF9FAFB',
      _.isEqual(session?.theme, ALLOWED_APP_THEMES.dark)
        ? '0xFFFFFF'
        : '0x0F1527',
      languageKey
    ]
  );
};

export const getDecipheredText = (session, text) => {
  if (!text) return text;

  const safeText = String(text);

  const ecik = `${session.eventid + session.userid}`
    .substring(0, 13)
    .split('')
    .map(Number);

  let result = '';
  for (let i = 0; i < safeText.length; i++) {
    let char = safeText.charCodeAt(i);
    let keyIndex = i % 13;
    let shift = ecik[keyIndex];
    if (char >= 65 && char <= 90) {
      // Uppercase letters
      result += String.fromCharCode(((char - 65 - shift + 26) % 26) + 65);
    } else if (char >= 97 && char <= 122) {
      // Lowercase letters
      result += String.fromCharCode(((char - 97 - shift + 26) % 26) + 97);
    } else {
      result += safeText[i];
    }
  }
  return result;
};

/**
 * @param category => State.speakerCategories value for all speaker categories
 * @param data => Selected speaker item record
 * @param isList => Boolean, default false
 * @returns JSX category tag
 */
export const _renderSpeakerCategory = (
  params = {
    category: {},
    data: {},
    inTheme: false
  }
) => {
  if (_.isEmpty(params?.category) || _.isEmpty(params?.data)) return <></>;

  const matchedCategory =
    !_.isEmpty(params.category) &&
    params.category?.find(
      ele => params?.data?.categoryid === ele?.id && ele?.isactive
    );

  return (
    !_.isEmpty(matchedCategory?.categorytitle) && (
      <span
        className={`px-2
          ${
            params?.inTheme
              ? 'custom-text10-normal-secondary text-center text-white mt-1'
              : 'py-1 custom-text1-normal-white'
          }`}
        style={{
          background: params?.inTheme
            ? 'var(--theme-color)'
            : 'rgba(0,0,0,0.4)',
          borderRadius: '7px',
          width: 'fit-content'
        }}
      >
        {matchedCategory?.categorytitle}
      </span>
    )
  );
};

/**
 * @param session
 * @param path Related DB path
 */
export const commonFileDownloader = async (session, path) => {
  if (_.isEmpty(path)) return;

  const isENC = isFileENC(path?.split('/').pop());
  const fileName = getFileNameFromPath(path);
  const relativeUrl = isENC
    ? replaceAll(
        ENC_FILES_URL,
        ['###PATH###', '###TOKEN###'],
        [path, session?.sessionToken]
      )
    : `${CDN_URL}${path}`;

  const link = document.createElement('a');
  link.href = relativeUrl;
  link.target = '_blank';
  link.setAttribute('download', fileName || 'file');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// SOURCE: widget codebase
export const getSpeakersOrdered = (
  speakerCategories = [],
  speakers = [],
  allList = false
) => {
  if (_.isEmpty(speakerCategories) || _.isEmpty(speakers)) return speakers;

  let returnList = [];
  let inActive = [];

  // Categorized
  _.map(speakerCategories, cate => {
    if (cate?.isactive) {
      returnList = _.concat(
        returnList,
        _.filter(speakers, ea => ea?.categoryid === cate?.id)
      );
    } else {
      inActive = _.concat(
        inActive,
        _.filter(speakers, ea => ea?.categoryid === cate?.id)
      );
    }
  });

  // Non-categorized
  returnList = _.concat(
    returnList,
    inActive,
    _.filter(speakers, ea => !ea?.categoryid)
  );

  // if (!_.isEmpty(REQ_PARAM.selectedSpkCates) && allList) {
  //   returnList = _.filter(returnList, spk =>
  //     _.includes(
  //       _.split(REQ_PARAM.selectedSpkCates, ',').map(Number),
  //       spk?.categoryid
  //     )
  //   );
  // }

  return returnList;
};

export const wrapWithScriptTags = input => {
  if (!input || typeof input !== 'string') return '';

  const trimmed = input.trim();
  const hasStart = trimmed.startsWith('<script>');
  const hasEnd = trimmed.endsWith('</script>');

  if (hasStart && hasEnd) {
    return trimmed; // Already correctly wrapped
  }

  if (!hasStart && !hasEnd) {
    return `<script>${trimmed}</script>`;
  }

  if (!hasStart && hasEnd) {
    return `<script>${trimmed}`;
  }

  if (hasStart && !hasEnd) {
    return `${trimmed}</script>`;
  }

  return trimmed; // fallback (shouldn't reach here logically)
};

// Helper to safely parse a date string or Date object
export const parseDate = val => {
  if (!val) return null;
  if (val instanceof Date && isValid(val)) return val;
  if (typeof val === 'string') {
    const parsed = parseISO(val);
    return isValid(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Handles image load before hand and returns image dimension
 *
 * @param url DB uri
 * @returns Loaded image dimension
 */
export const getUriImageSize = async url => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = err => {
      reject(err);
    };

    img.src = url;
  });
};

export const getSocialBoostRedirection = (checkLocal = false) => {
  let query = '',
    callAnalytics = false,
    socialId = null;

  if (window.location.hash.includes('?')) {
    query = window.location.hash.split('?').pop();
    socialId = new URLSearchParams(query).get('social');

    if (socialId) {
      query = replaceAll(
        SOCIAL_BOOST_QUERY_URL,
        ['###SOCIAL_ID###'],
        [socialId]
      );

      if (checkLocal) {
        const alreadyCalledAnalytics = loadFromLocalDB(
          PORTAL_LOCALSTORAGE_ACCESS_KEY.socialBoost
        );
        if (!_.isEmpty(alreadyCalledAnalytics)) {
          const foundSame = _.find(
            alreadyCalledAnalytics,
            item => item == socialId
          );
          if (!foundSame) {
            callAnalytics = true;
            setInLocalDB({
              key: PORTAL_LOCALSTORAGE_ACCESS_KEY.socialBoost,
              value: [...alreadyCalledAnalytics, socialId]
            });
          }
        } else {
          callAnalytics = true;
          setInLocalDB({
            key: PORTAL_LOCALSTORAGE_ACCESS_KEY.socialBoost,
            value: [socialId]
          });
        }
      }
    }
  }

  return { query, callAnalytics, socialId };
};

export const processInterestList = interestList => {
  return (
    _.chain(interestList)
      .map(item => {
        return {
          ...item,
          category_name: item.category_name ?? SESSION_NO_TRACK
        };
      })
      // .orderBy(
      //   ['category_display_order', 'interest_display_order'],
      //   ['asc', 'asc']
      // )
      .groupBy('category_name')
      .map((cateItems, cateName) => ({
        category_name: cateName,
        category_display_order: cateItems[0]?.category_display_order ?? 0,
        interests: cateItems
      }))
      .value()
  );
};

export const checkValidURL = urlString => {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};
